import { SelectItem } from '@flowx/angular-ui-toolkit'
import { UsersType } from '@flowx/designer/shared/entities/utils'
import { escapeHtml } from '@flowx/designer/shared/utils/string.utils'

import { ORGANIZATION_ADMIN_ROLE_ID, ORGANIZATION_OWNER_ROLE_ID } from '../commons.types'

export const buildUserHelperText = function (id: string): string | null {
  const options = {
    [ORGANIZATION_ADMIN_ROLE_ID]:
      ' This user will automatically get access to all workspace in the Organization.',
    [ORGANIZATION_OWNER_ROLE_ID]:
      ' Workspace-level roles do not apply for this role. They do not receive access to any workspace.',
  }
  return options[id] ?? null
}

export const buildUserImpactMessage = function (
  prev: string,
  current: string
): { title: string; description: string } | null {
  const title = `Impact of changing role: ${organizationRoleOptions[prev]?.label} → ${organizationRoleOptions[current]?.label}`
  const adminGainDescription =
    'The user will gain full administrative access to Organization and all Workspaces.'
  const ownerLoseDescription =
    "The user will lose Organization management capabilities. You'll need to assign at least one workspace role before saving."
  const options = {
    [`${ORGANIZATION_ADMIN_ROLE_ID}+${ORGANIZATION_OWNER_ROLE_ID}`]: {
      title,
      description: adminGainDescription,
    },
    [`${ORGANIZATION_ADMIN_ROLE_ID}+`]: {
      title,
      description: ownerLoseDescription,
    },
    [`${ORGANIZATION_OWNER_ROLE_ID}+${ORGANIZATION_ADMIN_ROLE_ID}`]: {
      title,
      description: adminGainDescription,
    },
    [`${ORGANIZATION_OWNER_ROLE_ID}+`]: {
      title,
      description: ownerLoseDescription,
    },
    [`+${ORGANIZATION_ADMIN_ROLE_ID}`]: {
      title,
      description: adminGainDescription,
    },
    [`+${ORGANIZATION_OWNER_ROLE_ID}`]: {
      title,
      description:
        'The user will gain Organization management capabilities, but they will lose access to Workspaces and Projects/Libraries.',
    },
  }
  return options[`${prev}+${current}`] || null
}
export const organizationRoleOptions: {
  [id: string]: SelectItem<string> & { description: string }
} = {
  ['']: {
    label: 'Workspace Member',
    description: 'Has access to one or multiple workspaces only',
    value: '',
  },
  [ORGANIZATION_ADMIN_ROLE_ID]: {
    label: 'Organization Admin',
    description: 'Has full administrative access to Organization and workspaces',
    value: ORGANIZATION_ADMIN_ROLE_ID,
  },
  [ORGANIZATION_OWNER_ROLE_ID]: {
    label: 'Organization Owner',
    description: 'Has access to manage users, solutions and monitor usage',
    value: ORGANIZATION_OWNER_ROLE_ID,
  },
}

const TOKEN_SYNCED_MEMBERSHIP_WARNING = `This user was added to the group automatically through the identity provider sync. If you remove them here, they will be added back the next time they use FlowX, unless they’re also removed from this group in the identity provider.`

const getGroupAccessImpactMessage = (
  isRuntime: boolean,
  safeGroupName: string,
  hasResourceAccess: boolean
): string => {
  if (!hasResourceAccess) {
    const resourceLabel = isRuntime ? 'solutions' : 'projects or libraries'
    return `The group <strong>${safeGroupName}</strong> currently has no access to any ${resourceLabel}. Removing the user from this group will not affect their access.`
  }

  return isRuntime
    ? `Removing this user from the group <strong>${safeGroupName}</strong> revokes access to all solutions shared with the group. The user may retain access if they have an individual role assigned.`
    : `Removing this user from the group <strong>${safeGroupName}</strong> will revoke any access to projects and libraries inherited through the group.`
}

export const getRemoveUserFromGroupImpactMessage = (
  userType: UsersType,
  groupName: string,
  hasResourceAccess: boolean,
  addedViaTokenSync = false
): string => {
  const isRuntime = userType === UsersType.RUNTIME
  const accessImpact = getGroupAccessImpactMessage(
    isRuntime,
    escapeHtml(groupName),
    hasResourceAccess
  )

  if (isRuntime && addedViaTokenSync) {
    return `${accessImpact}<br><br>${TOKEN_SYNCED_MEMBERSHIP_WARNING}`
  }
  return accessImpact
}
